import { useEffect, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { copyText, asMarkdown, asLinkedIn } from "@/lib/clipboard";
import {
  FloppyDisk, DownloadSimple, Share, Copy, ArrowsClockwise, MarkdownLogo, BellRinging, BellSlash,
  LinkedinLogo, FileDoc, Lock, TrashSimple, SignOut, Key,
} from "@phosphor-icons/react";


function NotifRow({ checked, onChange, disabled, testid, title, copy }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none py-2.5 border-t border-[var(--brand-ink)]/10 first:border-t-0">
      <input type="checkbox" checked={!!checked} onChange={onChange} disabled={disabled}
        data-testid={testid} className="mt-1 w-4 h-4 cursor-pointer accent-[var(--brand-primary)]"/>
      <div className="flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-[var(--brand-muted)] leading-relaxed">{copy}</p>
      </div>
    </label>
  );
}

function NotificationPrefsCard() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/notifications/prefs")
      .then(({ data }) => setPrefs(data))
      .catch(() => setPrefs({ email_reminders: true, marketing_opt_in: false, monthly_checkin: true }));
    // mount-only: api is a stable singleton, setPrefs is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = async (key, value) => {
    setBusy(true);
    try {
      const { data } = await api.put("/notifications/prefs", { [key]: value });
      setPrefs(data);
      toast.success("Preference saved");
    } catch {
      toast.error("Couldn't update preference");
    } finally { setBusy(false); }
  };

  if (!prefs) return null;

  return (
    <div className="card-hard rounded-md p-6 bg-[var(--pastel-yellow)]" data-testid="notification-prefs-card">
      <p className="label-tiny mb-2 flex items-center gap-1.5">
        <BellRinging size={12} weight="duotone"/> /notifications &amp; consent
      </p>
      <h3 className="font-display text-2xl font-semibold tracking-tight">email preferences.</h3>
      <p className="text-sm text-[var(--brand-muted)] mt-2 mb-3">
        granular control — flip any off, none required for the service to work.
      </p>
      <div className="mt-4">
        <NotifRow checked={prefs.email_reminders} disabled={busy} testid="prefs-email-reminders"
          onChange={(e) => update("email_reminders", e.target.checked)}
          title="document expiry reminders"
          copy="we ping you when documents in your vault are about to expire (passports, visas, certs). transactional, no marketing."/>
        <NotifRow checked={prefs.monthly_checkin} disabled={busy} testid="prefs-monthly-checkin"
          onChange={(e) => update("monthly_checkin", e.target.checked)}
          title="monthly check-in digest"
          copy="a once-a-month summary of your applications + a gentle 'how are you doing'. opt out any time."/>
        <NotifRow checked={prefs.marketing_opt_in} disabled={busy} testid="prefs-marketing"
          onChange={(e) => update("marketing_opt_in", e.target.checked)}
          title="marketing emails (new features, tips)"
          copy="opt-in only — UK GDPR consent. We'll never sell your email. Easy one-click unsubscribe in every email."/>
      </div>
    </div>
  );
}


function AIConsentResetCard() {
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    try { setHasConsent(localStorage.getItem("jv_ai_consent_v1") === "1"); } catch { /* noop */ }
  }, []);

  const reset = () => {
    try { localStorage.removeItem("jv_ai_consent_v1"); } catch { /* noop */ }
    setHasConsent(false);
    toast.success("AI consent reset — you'll be asked again on next use");
  };

  return (
    <div className="card-hard rounded-md p-6 bg-white" data-testid="ai-consent-card">
      <p className="label-tiny mb-2 flex items-center gap-1.5"><Lock size={12} weight="duotone"/> /AI consent</p>
      <h3 className="font-display text-2xl font-semibold tracking-tight">AI processing.</h3>
      <p className="text-sm text-[var(--brand-muted)] mt-2 mb-4">
        Vault Coach + Culture Barometer send the text you&apos;re working with to Anthropic Claude.
        Status: <b className="text-[var(--brand-ink)]">{hasConsent ? "consented" : "not yet consented"}</b>.
      </p>
      <button onClick={reset} disabled={!hasConsent} data-testid="reset-ai-consent"
        className="btn-hard bg-white rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
        <ArrowsClockwise size={14} weight="bold"/> reset AI consent
      </button>
    </div>
  );
}


function TransparencyCard() {
  const { user } = useAuth();
  const last = user?.last_login_at;
  const created = user?.created_at;
  const tos = user?.tos_accepted_at;

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="card-hard rounded-md p-6 bg-[var(--pastel-mint)]" data-testid="transparency-card">
      <p className="label-tiny mb-2 flex items-center gap-1.5"><BellRinging size={12} weight="duotone"/> /transparency</p>
      <h3 className="font-display text-2xl font-semibold tracking-tight">your account in numbers.</h3>
      <p className="text-sm text-[var(--brand-muted)] mt-2 mb-4">
        small facts about your account so you can spot anything weird.
      </p>
      <dl className="text-sm space-y-2">
        <div className="flex justify-between gap-3"><dt className="text-[var(--brand-muted)]">last login</dt><dd className="font-mono-d text-xs" data-testid="transparency-last-login">{fmt(last)}</dd></div>
        <div className="flex justify-between gap-3"><dt className="text-[var(--brand-muted)]">account created</dt><dd className="font-mono-d text-xs">{fmt(created)}</dd></div>
        <div className="flex justify-between gap-3"><dt className="text-[var(--brand-muted)]">terms accepted on</dt><dd className="font-mono-d text-xs">{fmt(tos)}</dd></div>
      </dl>
    </div>
  );
}


function ShareCard() {
  const [share, setShare] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: st }] = await Promise.all([
      api.get("/share/me"),
      api.get("/share/stats").catch(() => ({ data: null })),
    ]);
    setShare(s); setStats(st);
  };
  useEffect(() => { load(); }, []);

  const enable = async (showDocs) => {
    setBusy(true);
    try { await api.post("/share/enable", { show_documents: showDocs }); await load(); toast.success("Share link live"); }
    catch { toast.error("Couldn't enable"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await api.post("/share/disable"); await load(); toast.success("Share link disabled"); }
    finally { setBusy(false); }
  };
  const toggleDocs = async () => { if (!share?.enabled) return; await enable(!share.show_documents); };
  const regenerate = async () => {
    if (!confirm("Generate a new URL? The old one will stop working.")) return;
    try { await api.post("/share/regenerate"); await load(); toast.success("New link generated"); }
    catch { toast.error("Couldn't regenerate"); }
  };
  // Always build share links against the canonical brand domain so users
  // never see the preview subdomain when copying / sharing. Set
  // REACT_APP_PUBLIC_URL to override (e.g. for staging environments).
  const SHARE_BASE = (process.env.REACT_APP_PUBLIC_URL || "https://jobvault.co.uk").replace(/\/$/, "");
  const copyUrl = () => {
    if (!share?.slug) return;
    const url = `${SHARE_BASE}/v/${share.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  if (!share) return null;
  const url = share.slug ? `${SHARE_BASE}/v/${share.slug}` : "";

  return (
    <div className="card-hard rounded-md p-6 bg-[var(--pastel-peach)]" data-testid="share-card">
      <p className="label-tiny mb-2 flex items-center gap-1.5"><Share size={12} weight="duotone"/> /public share</p>
      <h3 className="font-display text-2xl font-semibold tracking-tight">share your vault.</h3>
      <p className="text-sm text-[var(--brand-muted)] mt-2">
        a public, read-only link recruiters can open without an account.
      </p>
      {!share.enabled ? (
        <button onClick={() => enable(false)} disabled={busy} data-testid="share-enable"
          className="mt-5 btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2">
          <Share size={16} weight="bold"/> enable share link
        </button>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="bg-white border-[1.5px] border-[var(--brand-ink)] rounded-md p-3 flex items-center gap-2">
            <code className="text-sm font-mono-d truncate flex-1" data-testid="share-url">{url}</code>
            <button onClick={copyUrl} data-testid="share-copy" className="p-1.5 hover:bg-[var(--pastel-yellow)] rounded"><Copy size={16}/></button>
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs underline">open</a>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={share.show_documents} onChange={toggleDocs} data-testid="share-show-docs"/>
            include documents on public page
          </label>
          <div className="flex gap-2 flex-wrap">
            <button onClick={regenerate} data-testid="share-regenerate" className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
              <ArrowsClockwise size={14}/> regenerate URL
            </button>
            <button onClick={disable} disabled={busy} data-testid="share-disable" className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs">disable</button>
          </div>
          {stats && (stats.views > 0 || stats.signups > 0) && (
            <div className="mt-2 pt-3 border-t-[1.5px] border-[var(--brand-ink)]/30" data-testid="share-stats">
              <p className="label-tiny mb-2">/your share, in numbers</p>
              <div className="flex flex-wrap gap-4">
                <div><div className="font-display text-2xl font-semibold tracking-tight">{stats.views}</div><p className="text-xs text-[var(--brand-muted)]">views</p></div>
                <div><div className="font-display text-2xl font-semibold tracking-tight">{stats.signups}</div><p className="text-xs text-[var(--brand-muted)]">sign-ups via your link</p></div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function ExportCard() {
  const exportJson = async () => {
    const { data } = await api.get("/export/profile");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vault-export-${Date.now()}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };
  const exportDocx = async () => {
    try {
      const res = await api.get("/export/docx", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `vault-${Date.now()}.docx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Word doc downloaded");
    } catch { toast.error("Couldn't generate Word doc"); }
  };
  const copyMd = async () => {
    const { data } = await api.get("/export/profile");
    copyText(asMarkdown.fullVault(data), "Vault copied as Markdown");
  };
  const copyLinkedIn = async () => {
    const { data } = await api.get("/export/profile");
    copyText(asLinkedIn.fullVault(data), "Copied for LinkedIn");
  };

  return (
    <div className="card-hard rounded-md p-6 bg-[var(--pastel-mint)]" data-testid="export-card">
      <p className="label-tiny mb-2">/export</p>
      <h3 className="font-display text-2xl font-semibold tracking-tight">take it with you.</h3>
      <p className="text-sm text-[var(--brand-muted)] mt-2">
        your vault, anywhere. paste into Notion, Google Docs, LinkedIn. or download a polished Word doc.
      </p>
      <div className="mt-5 space-y-2">
        <button onClick={exportDocx} data-testid="export-docx"
          className="w-full btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2">
          <FileDoc size={16} weight="bold"/> download .docx (Word / Google Docs)
        </button>
        <button onClick={copyMd} data-testid="export-markdown"
          className="w-full btn-hard bg-white text-[var(--brand-ink)] rounded-md px-4 py-2.5 text-sm flex items-center gap-2">
          <MarkdownLogo size={16} weight="bold"/> copy as Markdown (Notion-ready)
        </button>
        <button onClick={copyLinkedIn} data-testid="export-linkedin"
          className="w-full btn-hard bg-[#0A66C2] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2">
          <LinkedinLogo size={16} weight="bold"/> copy for LinkedIn (plain text)
        </button>
        <button onClick={exportJson} data-testid="export-json"
          className="w-full btn-hard bg-white text-[var(--brand-ink)] rounded-md px-4 py-2.5 text-sm flex items-center gap-2">
          <DownloadSimple size={16} weight="bold"/> download .json (developer / extension)
        </button>
      </div>
    </div>
  );
}


function AccountCard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState(null);

  useEffect(() => {
    setPendingDeletion(user?.deletion_requested_at || null);
  }, [user?.deletion_requested_at]);

  const requestPasswordReset = async () => {
    try {
      const { data: me } = await api.get("/auth/me");
      if (!me?.email) return toast.error("Can't read your email — try logging out and back in");
      await api.post("/auth/forgot-password", { email: me.email });
      toast.success("Reset link sent to your email");
    } catch { toast.error("Couldn't send reset link"); }
  };

  const requestDeletion = async () => {
    const confirmation = window.prompt(
      "Type DELETE to schedule your account for permanent erasure.\n\n" +
      "You'll have a 14-day grace window — just log back in to cancel.\n" +
      "After 14 days everything is wiped permanently."
    );
    if (confirmation !== "DELETE") return;
    setBusy(true);
    try {
      const { data } = await api.delete("/auth/me");
      toast.success(`Account scheduled for deletion on ${new Date(data.scheduled_for).toLocaleDateString()}. Log back in to cancel.`);
      await logout();
      nav("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete — contact support");
    } finally { setBusy(false); }
  };

  const cancelDeletion = async () => {
    setBusy(true);
    try {
      await api.post("/auth/cancel-deletion");
      setPendingDeletion(null);
      toast.success("Deletion cancelled — welcome back");
    } catch {
      toast.error("Couldn't cancel — please contact support");
    } finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="card-hard rounded-md p-6 bg-white" data-testid="account-card">
      <p className="label-tiny mb-2 flex items-center gap-1.5"><Lock size={12} weight="duotone"/> /account</p>
      <h3 className="font-display text-2xl font-semibold tracking-tight">account &amp; security.</h3>
      <p className="text-sm text-[var(--brand-muted)] mt-2">
        change your password, sign out everywhere, or schedule your vault for deletion.
      </p>

      {pendingDeletion && (
        <div className="mt-4 card-hard rounded-md p-4 bg-[var(--pastel-peach)]" data-testid="pending-deletion-banner">
          <p className="text-sm font-medium mb-1">⚠️ deletion scheduled</p>
          <p className="text-xs text-[var(--brand-muted)] mb-3">
            your account is set to be permanently erased in 14 days. log back in any time before then — or click below — to cancel.
          </p>
          <button onClick={cancelDeletion} disabled={busy} data-testid="cancel-deletion"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-1.5 text-xs font-medium">
            cancel deletion
          </button>
        </div>
      )}

      <div className="mt-5 space-y-2">
        <button onClick={requestPasswordReset} data-testid="settings-password-reset"
          className="w-full btn-hard bg-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2">
          <Key size={16} weight="bold"/> change password (sends reset email)
        </button>
        <button onClick={doLogout} data-testid="settings-logout"
          className="w-full btn-hard bg-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2">
          <SignOut size={16} weight="bold"/> log out
        </button>
        {!pendingDeletion && (
          <button onClick={requestDeletion} disabled={busy} data-testid="settings-delete-account"
            className="w-full btn-hard bg-[#B91C1C] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
            <TrashSimple size={16} weight="bold"/> {busy ? "scheduling…" : "delete my account (14-day grace)"}
          </button>
        )}
      </div>
    </div>
  );
}


export default function Settings() {
  return (
    <AppShell title="Settings">
      <p className="text-[var(--brand-muted)] mb-8 max-w-2xl">
        notifications, share-link, exports, AI consent, transparency + the &ldquo;delete my account&rdquo; button.
        identity bits live on Profile.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <NotificationPrefsCard/>
        <ShareCard/>
        <ExportCard/>
        <AIConsentResetCard/>
        <TransparencyCard/>
        <AccountCard/>
      </div>
    </AppShell>
  );
}
